'use client';

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// TypeScript interfaces based on the new collection_stats structure
export interface FirestoreCollectionStats {
  collection: string;
  /**
   * Secondary-market floor. NULL-vs-ZERO (WS-K): `null` = unknown / no recent
   * sales (OpenSea omitted the field); `0` = a genuine measured zero floor.
   * Consumers MUST distinguish — guard with `!= null && > 0` before formatting,
   * and never render a `null`/`0` floor as a fabricated "0.0000 ETH".
   */
  floorPrice: number | null;
  floorPriceSymbol?: string;
  totalVolume: number; // Now represents 24h volume (matches OpenSea UI)
  totalVolumeAllTime?: number; // All-time total volume
  totalSales: number;
  totalSupply: number;
  numOwners: number;
  averagePrice: number;
  marketCap: number;
  oneDayVolume?: number;
  oneDayChange?: number;
  oneDaySales?: number;
  sevenDayVolume?: number;
  sevenDayChange?: number;
  sevenDaySales?: number;
  thirtyDayVolume?: number;
  thirtyDayChange?: number;
  thirtyDaySales?: number;
  timestamp?: string;
  formattedVolume?: string;
  formattedFloorPrice?: string;
  formattedAveragePrice?: string;
  pollingTimestamp?: string;
  dataSource?: string;
  
  // Rich metadata from OpenSea collection endpoint
  description?: string | null;
  imageUrl?: string | null;
  bannerImageUrl?: string | null;
  externalUrl?: string | null;
  twitterUsername?: string | null;
  discordUrl?: string | null;
  instagramUsername?: string | null;
  wikiUrl?: string | null;
  createdDate?: string | null;
  category?: string | null;
  isVerified?: boolean;
  contracts?: Array<{ address: string; chain: string }>;
}

interface UseFirestoreCollectionStatsOptions {
  enableRealTimeUpdates?: boolean;
}

interface UseFirestoreCollectionStatsReturn {
  allStats: Record<string, FirestoreCollectionStats>;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isRealTime: boolean;
}

export function useFirestoreCollectionStats(options: UseFirestoreCollectionStatsOptions = {}): UseFirestoreCollectionStatsReturn {
  const { enableRealTimeUpdates = true } = options;

  const [allStats, setAllStats] = useState<Record<string, FirestoreCollectionStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    // Skip during build time or when Firebase is not available
    if (!enableRealTimeUpdates || !db || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Set up real-time listener for all collection stats
      const unsubscribe = onSnapshot(
        collection(db, 'collection_stats'),
        (snapshot) => {
          try {
            const statsMap: Record<string, FirestoreCollectionStats> = {};

            snapshot.forEach((doc) => {
              const statsData = doc.data() as FirestoreCollectionStats;
              // Use collection name as the key for easy lookup
              statsMap[statsData.collection] = {
                ...statsData,
                // Ensure we have formatted versions for display
                formattedVolume: statsData.formattedVolume || `${statsData.totalVolume.toFixed(2)} ETH`,
                // NULL-vs-ZERO (WS-K): an unknown floor (null) formats as a dash, not "0.000".
                formattedFloorPrice:
                  statsData.formattedFloorPrice ||
                  (statsData.floorPrice != null ? `${statsData.floorPrice.toFixed(3)} ETH` : '— ETH'),
              };
            });

            // Collection stats received successfully
            
            setAllStats(statsMap);
            setLastUpdated(new Date());
            setError(null);
            setLoading(false);
          } catch (err) {
            // Error processing collection stats snapshot
            setError('Failed to process collection stats data');
            setLoading(false);
          }
        },
        (err) => {
          // Firestore collection stats listener error - handle gracefully
          console.warn('Firestore collection stats listener error:', err.message);
          setError(`Failed to fetch collection stats: ${err.message}`);
          setLoading(false);
        }
      );

      // Cleanup listener on unmount
      return () => {
        // Cleaning up Firestore collection stats listener
        unsubscribe();
      };
    } catch (err) {
      // Error setting up Firestore collection stats listener - handle gracefully
      console.warn('Failed to initialize collection stats listener:', err);
      setError('Failed to initialize collection stats listener');
      setLoading(false);
    }
  }, [enableRealTimeUpdates]);

  return {
    allStats,
    loading,
    error,
    lastUpdated,
    isRealTime: enableRealTimeUpdates,
  };
}

// Helper function to get stats for a specific collection
export function useFirestoreCollectionStat(collectionName: string) {
  const { allStats, loading, error } = useFirestoreCollectionStats();
  
  return {
    stats: allStats[collectionName] || null,
    loading,
    error,
    hasStats: !!allStats[collectionName],
  };
}

// Helper function to format collection stats for display
export function formatCollectionStats(stats: FirestoreCollectionStats) {
  const timestamp = stats.pollingTimestamp || stats.timestamp;
  
  return {
    ...stats,
    formattedTotalSales: stats.totalSales.toLocaleString(),
    formattedMarketCap: `${stats.marketCap.toFixed(2)} ETH`,
    formattedAveragePrice: `${stats.averagePrice.toFixed(3)} ETH`,
    formattedNumOwners: stats.numOwners.toLocaleString(),
    formattedTotalSupply: stats.totalSupply.toLocaleString(),
    lastUpdatedAgo: timestamp ? getTimeAgo(new Date(timestamp)) : 'N/A',
  };
}

// Helper function to get time ago string
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
}
